/**
 * Centralized Logger Service for BeCreatives Frontend
 * Replaces scattered console.log, console.error, etc. throughout the codebase
 * 
 * Features:
 * - Configurable log levels based on environment
 * - Timestamps for all log entries
 * - Component/origin tracking
 * - Production-grade logging with proper filtering
 * - Easy to extend and modify from one place
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4, // Suppress all logging
}

interface LoggerConfig {
  level: LogLevel;
  enableTimestamps: boolean;
  enableOrigin: boolean;
  prefix?: string;
}

class LoggerService {
  private config: LoggerConfig;

  constructor(config?: Partial<LoggerConfig>) {
    // Default configuration with environment-based log level
    this.config = {
      level: this.getDefaultLogLevel(),
      enableTimestamps: true,
      enableOrigin: true,
      prefix: '[BeCreatives]',
      ...config,
    };
  }

  /**
   * Determine default log level based on environment
   */
  private getDefaultLogLevel(): LogLevel {
    // Check for Vite environment variables
    if (import.meta.env.PROD) {
      return LogLevel.ERROR; // Only errors in production
    }
    
    if (import.meta.env.DEV) {
      return LogLevel.DEBUG; // All logs in development
    }

    // Check for NODE_ENV fallback
    const nodeEnv = import.meta.env.NODE_ENV || 'development';
    return nodeEnv === 'production' ? LogLevel.ERROR : LogLevel.DEBUG;
  }

  /**
   * Get current timestamp formatted for logging
   */
  private getTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Format log message with optional metadata
   */
  private formatMessage(
    level: string,
    message: string,
    origin?: string,
    ...optionalParams: any[]
  ): [string, ...any[]] {
    const parts: string[] = [];
    
    if (this.config.prefix) {
      parts.push(this.config.prefix);
    }
    
    if (this.config.enableTimestamps) {
      parts.push(`[${this.getTimestamp()}]`);
    }
    
    parts.push(`[${level}]`);
    
    if (this.config.enableOrigin && origin) {
      parts.push(`[${origin}]`);
    }
    
    const formattedMessage = `${parts.join(' ')} ${message}`;
    
    return [formattedMessage, ...optionalParams];
  }

  /**
   * Check if log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  /**
   * Debug level logging - most verbose
   * @param message - The debug message
   * @param origin - Optional component/origin name
   * @param optionalParams - Additional parameters to log
   */
  debug(message: string, origin?: string, ...optionalParams: any[]): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    
    const formatted = this.formatMessage('DEBUG', message, origin, ...optionalParams);
    console.debug(...formatted);
  }

  /**
   * Info level logging - general information
   * @param message - The info message
   * @param origin - Optional component/origin name
   * @param optionalParams - Additional parameters to log
   */
  info(message: string, origin?: string, ...optionalParams: any[]): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    
    const formatted = this.formatMessage('INFO', message, origin, ...optionalParams);
    console.info(...formatted);
  }

  /**
   * Warning level logging - potential issues
   * @param message - The warning message
   * @param origin - Optional component/origin name
   * @param optionalParams - Additional parameters to log
   */
  warn(message: string, origin?: string, ...optionalParams: any[]): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    
    const formatted = this.formatMessage('WARN', message, origin, ...optionalParams);
    console.warn(...formatted);
  }

  /**
   * Error level logging - serious issues
   * @param message - The error message
   * @param origin - Optional component/origin name
   * @param optionalParams - Additional parameters to log (errors, stack traces, etc.)
   */
  error(message: string, origin?: string, ...optionalParams: any[]): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const formatted = this.formatMessage('ERROR', message, origin, ...optionalParams);
    console.error(...formatted);
  }

  /**
   * Update logger configuration at runtime
   * @param newConfig - Partial configuration to update
   */
  configure(newConfig: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }

  /**
   * Set log level programmatically
   * @param level - New log level
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * Create a scoped logger with predefined origin
   * @param origin - Component/module name that will be automatically included
   * @returns Scoped logger instance
   */
  createScopedLogger(origin: string) {
    return {
      debug: (message: string, ...optionalParams: any[]) => 
        this.debug(message, origin, ...optionalParams),
      info: (message: string, ...optionalParams: any[]) => 
        this.info(message, origin, ...optionalParams),
      warn: (message: string, ...optionalParams: any[]) => 
        this.warn(message, origin, ...optionalParams),
      error: (message: string, ...optionalParams: any[]) => 
        this.error(message, origin, ...optionalParams),
    };
  }
}

// Create and export default logger instance
export const Logger = new LoggerService();

// Export LoggerService class for custom instances
export { LoggerService };

// Convenience exports for direct usage
export const logger = Logger;
export default Logger;
